import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRightLeft,
  Mic,
  MicOff,
  MoreHorizontal,
  Headphones,
  HeadphoneOff,
  PhoneOff,
  ScreenShare,
  Video,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useLiveStream } from '@/lib/ws';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useGuildRights } from '@/lib/access';

/**
 * Live voice-channel occupancy for a guild.
 *
 * Initial state comes from GET /api/guilds/:id/voice-states (Redis-cached
 * snapshot). Every subsequent voiceStateUpdate in the bot triggers a
 * republish on bot:event, streamed via /ws/live — we apply matching updates
 * in-place.
 */

interface VoiceMember {
  id: string;
  username: string;
  avatarUrl: string | null;
  selfMute: boolean;
  selfDeaf: boolean;
  serverMute: boolean;
  serverDeaf: boolean;
  streaming: boolean;
  video: boolean;
}

interface VoiceChannel {
  id: string;
  name: string;
  categoryName: string | null;
  categoryId: string | null;
  position: number;
  userLimit: number;
  memberCount: number;
  members: VoiceMember[];
}

interface VoiceSnapshot {
  guildId: string;
  channels: VoiceChannel[];
  updatedAt: string | null;
}

export function VoicePresence() {
  const { guildId } = useParams<{ guildId: string }>();
  const initial = useQuery({
    queryKey: ['voice-states', guildId],
    queryFn: () => api<{ snapshot: VoiceSnapshot }>(`/api/guilds/${guildId}/voice-states`),
    enabled: !!guildId,
  });
  const { messages, connected } = useLiveStream(200);
  const [snapshot, setSnapshot] = useState<VoiceSnapshot | null>(null);

  useEffect(() => {
    if (initial.data?.snapshot) setSnapshot(initial.data.snapshot);
  }, [initial.data]);

  useEffect(() => {
    // Consume any voice-state events matching this guildId from the stream.
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.channel !== 'bot:event') continue;
      const p = m.payload as { type?: string; guildId?: string; snapshot?: VoiceSnapshot };
      if (p?.type === 'voice-state' && p.guildId === guildId && p.snapshot) {
        setSnapshot((prev) =>
          prev && prev.updatedAt && p.snapshot!.updatedAt && p.snapshot!.updatedAt < prev.updatedAt
            ? prev
            : p.snapshot!
        );
        return;
      }
    }
  }, [messages, guildId]);

  const grouped = useMemo(() => {
    if (!snapshot) return new Map<string, { name: string; channels: VoiceChannel[] }>();
    const map = new Map<string, { name: string; channels: VoiceChannel[] }>();
    for (const ch of snapshot.channels) {
      const key = ch.categoryId ?? '__none__';
      const bucket = map.get(key) ?? {
        name: ch.categoryName ?? 'Ohne Kategorie',
        channels: [],
      };
      bucket.channels.push(ch);
      map.set(key, bucket);
    }
    return map;
  }, [snapshot]);

  const totalMembers = snapshot?.channels.reduce((n, c) => n + c.memberCount, 0) ?? 0;
  const activeChannels =
    snapshot?.channels.filter((c) => c.memberCount > 0).length ?? 0;

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Voice-Belegung</h1>
        <div className="text-xs text-muted-foreground">
          <span
            className={`mr-2 inline-block h-2 w-2 rounded-full ${
              connected ? 'bg-primary' : 'bg-muted'
            }`}
          />
          {connected ? 'Live' : 'Verbinde…'}
          {snapshot?.updatedAt && (
            <span className="ml-3">
              Aktualisiert:{' '}
              {new Date(snapshot.updatedAt).toLocaleTimeString('de-DE')}
            </span>
          )}
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-semibold">{totalMembers}</div>
            <div className="text-xs text-muted-foreground">User in Voice</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-semibold">{activeChannels}</div>
            <div className="text-xs text-muted-foreground">Aktive Channels</div>
          </CardContent>
        </Card>
      </div>

      {initial.isLoading && <p className="text-muted-foreground">Lade Snapshot…</p>}
      {snapshot && snapshot.channels.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Noch kein Snapshot vorhanden. Sobald jemand einen Voice-Channel
          betritt oder der Bot neu startet, erscheinen die Kanäle hier.
        </p>
      )}

      <div className="space-y-4">
        {[...grouped.entries()].map(([catId, group]) => (
          <div key={catId}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {group.name}
            </h2>
            <div className="space-y-2">
              {group.channels.map((ch) => (
                <VoiceChannelCard
                  key={ch.id}
                  channel={ch}
                  guildId={guildId!}
                  allChannels={snapshot?.channels ?? []}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VoiceChannelCard({
  channel,
  guildId,
  allChannels,
}: {
  channel: VoiceChannel;
  guildId: string;
  allChannels: VoiceChannel[];
}) {
  const isEmpty = channel.memberCount === 0;
  return (
    <Card className={isEmpty ? 'opacity-60' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-muted-foreground" />
            {channel.name}
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {channel.memberCount}
            {channel.userLimit > 0 && ` / ${channel.userLimit}`}
          </span>
        </CardTitle>
      </CardHeader>
      {!isEmpty && (
        <CardContent>
          <ul className="space-y-1.5">
            {channel.members.map((m) => (
              <VoiceMemberRow
                key={m.id}
                member={m}
                guildId={guildId}
                currentChannelId={channel.id}
                allChannels={allChannels}
              />
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}

function VoiceMemberRow({
  member,
  guildId,
  currentChannelId,
  allChannels,
}: {
  member: VoiceMember;
  guildId: string;
  currentChannelId: string;
  allChannels: VoiceChannel[];
}) {
  const [open, setOpen] = useState<null | 'menu' | 'move'>(null);
  // VC actions belong to Voice-Manager, not to this page (lib/access.ts).
  const canAct = useGuildRights(guildId).can('voiceManager', 'edit');

  const doAction = useMutation({
    mutationFn: (payload: { path: string; body: object }) =>
      api(`/api/actions/${payload.path}`, {
        method: 'POST',
        body: JSON.stringify(payload.body),
      }),
    onSuccess: () => setOpen(null),
  });

  const moveTargets = allChannels.filter((c) => c.id !== currentChannelId);
  const isServerMuted = member.serverMute;

  return (
    <li className="rounded-md py-1 text-sm">
      <div className="flex items-center gap-2">
        {member.avatarUrl ? (
          <img
            src={member.avatarUrl}
            alt=""
            className="h-6 w-6 rounded-full"
            loading="lazy"
          />
        ) : (
          <div className="h-6 w-6 rounded-full bg-muted" />
        )}
        <span className="min-w-0 flex-1 truncate">{member.username}</span>
        <VoiceStateIcons member={member} />
        {canAct && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setOpen(open ? null : 'menu')}
            title="Aktionen"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {open === 'menu' && (
        <div className="mt-2 flex flex-wrap gap-1 rounded-md border bg-muted/40 p-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={doAction.isPending}
            onClick={() => {
              if (isServerMuted) {
                doAction.mutate({
                  path: 'vc-unmute',
                  body: { guildId, userId: member.id },
                });
              } else {
                doAction.mutate({
                  path: 'vc-mute',
                  body: { guildId, userId: member.id },
                });
              }
            }}
          >
            {isServerMuted ? (
              <>
                <Volume2 className="mr-1 h-3.5 w-3.5" /> Entmuten
              </>
            ) : (
              <>
                <VolumeX className="mr-1 h-3.5 w-3.5" /> Muten
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setOpen('move')}
          >
            <ArrowRightLeft className="mr-1 h-3.5 w-3.5" /> Verschieben
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="h-7 text-xs"
            disabled={doAction.isPending}
            onClick={() => {
              if (confirm(`${member.username} aus Voice werfen?`)) {
                doAction.mutate({
                  path: 'vc-kick',
                  body: { guildId, userId: member.id },
                });
              }
            }}
          >
            <PhoneOff className="mr-1 h-3.5 w-3.5" /> Rauswerfen
          </Button>
        </div>
      )}

      {open === 'move' && (
        <div className="mt-2 rounded-md border bg-muted/40 p-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Nach welchem Channel?
          </div>
          {moveTargets.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Keine anderen Voice-Channels verfügbar.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {moveTargets.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() =>
                    doAction.mutate({
                      path: 'vc-move',
                      body: { guildId, userId: member.id, voiceChannelId: c.id },
                    })
                  }
                  disabled={doAction.isPending}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50'
                  )}
                  title={c.categoryName ?? ''}
                >
                  {c.name} <span className="opacity-60">({c.memberCount})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {doAction.error && (
        <p className="mt-1 text-xs text-destructive">
          {(doAction.error as Error).message}
        </p>
      )}
    </li>
  );
}

function VoiceStateIcons({ member }: { member: VoiceMember }) {
  const muted = member.selfMute || member.serverMute;
  const deafened = member.selfDeaf || member.serverDeaf;
  return (
    <span className="flex items-center gap-1 text-muted-foreground">
      {member.streaming && (
        <ScreenShare className="h-3.5 w-3.5 text-primary" aria-label="Streaming" />
      )}
      {member.video && (
        <Video className="h-3.5 w-3.5 text-primary" aria-label="Video an" />
      )}
      {muted ? (
        <MicOff
          className={`h-3.5 w-3.5 ${
            member.serverMute ? 'text-destructive' : ''
          }`}
          aria-label={member.serverMute ? 'Server-Mute' : 'Self-Mute'}
        />
      ) : (
        <Mic className="h-3.5 w-3.5 opacity-40" aria-hidden="true" />
      )}
      {deafened ? (
        <HeadphoneOff
          className={`h-3.5 w-3.5 ${
            member.serverDeaf ? 'text-destructive' : ''
          }`}
          aria-label={member.serverDeaf ? 'Server-Deaf' : 'Self-Deaf'}
        />
      ) : (
        <Headphones className="h-3.5 w-3.5 opacity-40" aria-hidden="true" />
      )}
    </span>
  );
}
